import { toPaymentMethod } from '@/api/mappers/mapPaymentMethod';
import {
  BillingAddress as ApiBillingAddress,
  BillingDetails as ApiBillingDetails,
  BillingInfo as ApiBillingInfo,
  BillingPricing as ApiBillingPricing,
  BillingPricingOption as ApiBillingPricingOption,
  BillingPricingPlan as ApiBillingPricingPlan,
  BillingPricingProduct as ApiBillingPricingProduct,
  UpdateSubscriptionRequest as ApiUpdateSubscriptionRequest,
} from '@/gen/api';
import {
  BillingAddress,
  BillingDetails,
  BillingInfo,
  BillingPricing,
  BillingPricingOption,
  BillingPricingPlan,
  BillingPricingProduct,
  SubscriptionUpdate,
} from '@/models/Billing';

export function toBillingInfo(resp: ApiBillingInfo): BillingInfo {
  return {
    ...resp,
    defaultPaymentMethod: resp.defaultPaymentMethod
      ? toPaymentMethod(resp.defaultPaymentMethod)
      : undefined,
  };
}

export function toBillingDetails(resp: ApiBillingDetails): BillingDetails {
  return {
    ...resp,
    address: resp.address ? toBillingAddress(resp.address) : undefined,
  };
}

export function toBillingAddress(api: ApiBillingAddress): BillingAddress {
  return {
    id: api.id ?? undefined,
    building: api.building,
    street1: api.street1,
    street2: api.street2,
    postalCode: api.postalCode,
    region: api.region,
    city: api.city,
    countryCode: api.countryCode,
  };
}

export function toBillingPricing(api: ApiBillingPricing): BillingPricing {
  return {
    ownerType: api.ownerType,
    plans: api.plans.map(toBillingPricingPlan),
  };
}

export function toBillingPricingPlan(api: ApiBillingPricingPlan): BillingPricingPlan {
  return {
    planCode: api.planCode,
    options: api.options.map(toBillingPricingOption),
  };
}

export function toBillingPricingOption(api: ApiBillingPricingOption): BillingPricingOption {
  return {
    countryCode: api.countryCode,
    currencyCode: api.currencyCode,
    products: api.products.map(toBillingPricingProduct),
  };
}

export function toBillingPricingProduct(api: ApiBillingPricingProduct): BillingPricingProduct {
  return {
    product: api.product,
    isActive: api.isActive,
    vatRate: api.vatRate,
    unitPrice: api.unitPrice,
    vatAmount: api.vatAmount,
    totalAmount: api.totalAmount,
  };
}

export function billingDetailsToApi(details: BillingDetails): ApiBillingDetails {
  return {
    ...details,
    companyName: details.companyName ?? '',
    billingEmail: details.billingEmail ?? '',
    address: details.address ? billingAddressToApi(details.address) : undefined,
  };
}

export function billingAddressToApi(address: BillingAddress): ApiBillingAddress {
  return {
    id: address.id ?? undefined,
    building: address.building,
    street1: address.street1,
    street2: address.street2,
    postalCode: address.postalCode,
    region: address.region,
    city: address.city,
    countryCode: address.countryCode,
  };
}

export function updateSubscriptionToApi(
  subscription: SubscriptionUpdate,
): ApiUpdateSubscriptionRequest {
  const billingCycle = subscription.plan === 'paid' ? subscription.billingCycle : undefined;

  return {
    customer: subscription.customer,
    plan: subscription.plan,
    billingCycle,
    startedAt: subscription.startedAt ?? undefined,
    cancelAtPeriodEnd: subscription.cancelAtPeriodEnd ?? undefined,
  };
}
